import { EventContentArg } from '@fullcalendar/core';
import { useState } from 'react';
import { RegularActivityCard } from './RegularActivityCard';
import { CustomAdventureCard } from './CustomAdventureCard';
import { SlotHoverCard } from './SlotHoverCard';

interface EnhancedCalendarEventContentProps {
  eventInfo: EventContentArg;
  onEditSlot?: (slotId: string, slotData: any) => void;
  onDeleteSlot?: (slotId: string, slotData: any) => void;
  onViewCustomAdventure?: (bookingId: string, bookingData: any) => void;
}

export function EnhancedCalendarEventContent({ 
  eventInfo, 
  onEditSlot, 
  onDeleteSlot,
  onViewCustomAdventure 
}: EnhancedCalendarEventContentProps) {
  const { event, view } = eventInfo;
  const isMonthView = view.type === 'dayGridMonth';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showHoverCard, setShowHoverCard] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  // Check if this is a custom adventure booking
  const isCustomAdventure = event.extendedProps?.type === 'custom-adventure' || 
                           event.extendedProps?.isCustomAdventure;

  // Get appropriate ID based on event type
  const eventId = isCustomAdventure 
    ? event.extendedProps?.bookingId 
    : (event.id || event.extendedProps?.slotId || event.extendedProps?.id);
  
  // Debug logging
  if (!eventId) {
    console.warn('🔍 Missing Event ID:', {
      eventId: event.id,
      isCustomAdventure,
      extendedProps: event.extendedProps,
      title: event.title
    });
  }

  // Handle menu actions based on event type
  const handleEdit = () => {
    if (isCustomAdventure && onViewCustomAdventure) {
      onViewCustomAdventure(eventId, event.extendedProps);
    } else if (onEditSlot) {
      onEditSlot(eventId, {
        id: eventId,
        title: event.title,
        start: event.start,
        end: event.end,
        ...event.extendedProps
      });
    }
    setIsMenuOpen(false);
  };

  const handleDelete = () => {
    if (!isCustomAdventure && onDeleteSlot) {
      onDeleteSlot(eventId, {
        id: eventId,
        title: event.title,
        start: event.start,
        end: event.end,
        ...event.extendedProps
      });
    }
    setIsMenuOpen(false);
  };

  // Handle hover for rich details
  const handleMouseEnter = (e: React.MouseEvent) => {
    if (isMonthView) {
      setHoverPosition({ x: e.clientX + 10, y: e.clientY + 10 });
      setShowHoverCard(true);
    }
  };

  const handleMouseLeave = () => {
    if (isMonthView) {
      setShowHoverCard(false);
    }
  };

  // Render custom adventure event using reusable component
  if (isCustomAdventure) {
    const participants = event.extendedProps?.participants;
    const totalParticipants = participants ? 
      (participants.adults || 0) + (participants.children || 0) + (participants.students || 0) : 0;
    
    return (
      <CustomAdventureCard
        title={event.title}
        totalParticipants={totalParticipants}
        customerName={event.extendedProps?.customerName}
        isMonthView={isMonthView}
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        onViewDetails={handleEdit}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Hover Card */}
        <SlotHoverCard
          event={event}
          isVisible={showHoverCard}
          position={hoverPosition}
          onEdit={onEditSlot}
          onDelete={onDeleteSlot}
          onViewCustomAdventure={onViewCustomAdventure}
          onClose={() => setShowHoverCard(false)}
        />
      </CustomAdventureCard>
    );
  }

  // Render regular activity event using reusable component
  const capacity = event.extendedProps?.capacity || 0;
  const booked = event.extendedProps?.booked || 0;

  return (
    <RegularActivityCard
      title={event.title}
      capacity={capacity}
      booked={booked}
      start={event.start || undefined}
      end={event.end || undefined}
      isMonthView={isMonthView}
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hover Card */}
      <SlotHoverCard
        event={event}
        isVisible={showHoverCard}
        position={hoverPosition}
        onEdit={onEditSlot}
        onDelete={onDeleteSlot}
        onViewCustomAdventure={onViewCustomAdventure}
        onClose={() => setShowHoverCard(false)}
      />
    </RegularActivityCard>
  );
}
